import utilServices from 'app/services/util-services'

/**
 * Client-side checks aligned with ClassService.ValidateClassDates (server is authoritative).
 * dxDateBox `type: 'date'` yields a Date at local midnight; comparing getTime() to API term
 * instants (UTC day boundaries) fails in non-UTC zones. Save uses toUtcStartOfDay / toUtcEndOfDay;
 * we apply the same here so validation matches what is sent and what the server checks.
 * @param {object} params
 * @param {*} params.dateStart
 * @param {*} params.dateEnd
 * @param {*} params.bookingStart
 * @param {*} params.bookingEnd
 * @param {*} params.termStart study start
 * @param {*} params.termEnd study end
 * @param {*} [params.termDateStart] term boundary from Term lookup (dateStart)
 * @param {*} [params.termDateEnd] term boundary from Term lookup (dateEnd)
 * @returns {string|null} first error message, or null if valid / insufficient data for cross-field checks
 */
export function validateClassDates(params) {
    const {
        dateStart,
        dateEnd,
        bookingStart,
        bookingEnd,
        termStart,
        termEnd,
        termDateStart,
        termDateEnd,
    } = params ?? {}

    const toMs = (v) => {
        if (v == null || v === '') return NaN
        const d = v instanceof Date ? v : new Date(v)
        const t = d.getTime()
        return Number.isNaN(t) ? NaN : t
    }

    /** Same UTC calendar day as Add/Update class + term save (util-services). */
    const startMs = (v) => {
        const d = utilServices.dateOnlyValueToLocalDate(v)
        if (d instanceof Date && !Number.isNaN(d.getTime())) {
            return d.toUtcStartOfDay().getTime()
        }
        return toMs(v)
    }
    const endMs = (v) => {
        const d = utilServices.dateOnlyValueToLocalDate(v)
        if (d instanceof Date && !Number.isNaN(d.getTime())) {
            return d.toUtcEndOfDay().getTime()
        }
        return toMs(v)
    }

    const overallStart = startMs(dateStart)
    const overallEnd = endMs(dateEnd)
    const bookS = startMs(bookingStart)
    const bookE = endMs(bookingEnd)
    const studyS = startMs(termStart)
    const studyE = endMs(termEnd)

    const core = [overallStart, overallEnd, bookS, bookE, studyS, studyE]
    if (core.some((x) => Number.isNaN(x))) return null

    if (overallStart > overallEnd) {
        return 'Overall date start must be before or on overall date end.'
    }

    const trS = startMs(termDateStart)
    const trE = endMs(termDateEnd)
    if (!Number.isNaN(trS) && !Number.isNaN(trE)) {
        if (overallStart < trS || overallEnd > trE) {
            return 'Overall class dates must fall within the term dates.'
        }
    }

    if (bookS > bookE) {
        return 'Booking start must be before or on booking end.'
    }

    if (bookS < overallStart || bookE > overallEnd) {
        return 'Booking dates must fall within the class overall dates (from overall date start through overall date end).'
    }

    if (studyS > studyE) {
        return 'Study start must be before or on study end.'
    }

    if (studyS < overallStart || studyE > overallEnd) {
        return 'Study dates must fall within the class overall dates (from overall date start through overall date end).'
    }

    return null
}
